import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { ICONS } from '@/constants/icons';
import { useAIBrandProfiles, useBrandProfileCreateGate } from '@/hooks/useAIBrandProfiles';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import { LockedFeatureCard } from '@/components/gating/LockedFeatureCard';
import { useTimezone } from '@/contexts/TimezoneContext';
import type { AIBrandProfile } from '@/types/ai';
import { AIBrandProfileCreateModal } from '@/components/ai-brand-profile/AIBrandProfileCreateModal';
import { AIBrandProfileEditModal } from '@/components/ai-brand-profile/AIBrandProfileEditModal';

/**
 * AI Profiles Section - Quick access to brand voice profiles
 * Shows list of AI brand profiles with edit links
 * Integrated with real data from useAIBrandProfiles hook
 */
export const AIProfilesSection = () => {
  const { t } = useTranslation(['dashboard', 'common', 'settings']);
  const navigate = useNavigate();
  const { profiles, isLoading } = useAIBrandProfiles();
  const { atPackageLimit, atSystemCap, showLimitUpsell } = useBrandProfileCreateGate();
  const { formatRelativeDate } = useTimezone();
  // Reviewer-only people may look at the Brand Worlds, not change them
  const { canManageBrandSettings: canManageByRole } = useReviewerRestrictions();
  // Plan gating (brand_world): existing Brand Worlds stay visible; creating / editing is locked
  const brandWorldLock = useFeatureLock('brand_world');
  const canManageBrandSettings = canManageByRole && !brandWorldLock.locked;

  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState<{ open: boolean; profile: AIBrandProfile | null }>({
    open: false,
    profile: null,
  });

  const handleNavigateToSettings = () => {
    navigate('/settings?tab=ai-profiles');
  };

  const handleOpenCreate = () => {
    if (atSystemCap) return;
    if (atPackageLimit) {
      showLimitUpsell();
      return;
    }
    setCreateModal(true);
  };

  const handleOpenEdit = (profile: AIBrandProfile) => {
    setEditModal({ open: true, profile });
  };

  // Loading state - show skeleton grid
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white border border-gray-100 rounded-xl p-4 animate-pulse">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-gray-200" />
              <div className="h-4 bg-gray-200 rounded w-24" />
            </div>
            <div className="h-3 bg-gray-100 rounded w-36" />
          </div>
        ))}
      </div>
    );
  }


  return (
    <div>
      {/* Empty state */}
      {profiles.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 bg-primary-50 rounded-full grid place-items-center mb-4">
            <Icon name={ICONS.WORLD} size="xl" color="text-primary-400" ariaHidden />
          </div>
          <p className="text-gray-500 text-sm mb-4">
            {t('dashboard_ai_profiles_empty_hint', { defaultValue: "A Brand World holds everything that makes your brand itself — identity, audience, values, voice, and the words you'd never use. Create one and Ed writes every post from inside it, so nothing sounds generic." })}
          </p>
          {canManageByRole && brandWorldLock.locked && (
            <LockedFeatureCard feature="brand_world" upsellPlanSlug={brandWorldLock.upsellPlanSlug} compact className="max-w-md w-full" />
          )}
          {canManageBrandSettings && (
            <button
              onClick={handleOpenCreate}
              className="px-4 py-2 rounded-lg text-sm font-medium text-primary-600-contrast bg-primary-600 hover:bg-primary-700 transition-colors flex items-center gap-2"
            >
              <Icon name={ICONS.PLUS} size="sm" ariaHidden />
              {t('dashboard_ai_profiles_create_first', { defaultValue: 'Create your first Brand World' })}
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Profiles Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {profiles.slice(0, 3).map((profile) => {
              const details = [
                profile.industry,
                profile.tone,
                profile.language,
              ].filter(Boolean);

              const usageText = profile.usageCount > 0
                ? t('dashboard_ai_profiles_used_times', { count: profile.usageCount, defaultValue: `Used ${profile.usageCount} times` })
                : t('dashboard_ai_profiles_not_used', { defaultValue: 'Not used yet' });
              const lastUsedText = profile.lastUsedAt
                ? t('dashboard_ai_profiles_last_used', { date: formatRelativeDate(profile.lastUsedAt), defaultValue: `Last used ${formatRelativeDate(profile.lastUsedAt)}` })
                : null;

              return (
                <div
                  key={profile.id}
                  onClick={canManageBrandSettings ? () => handleOpenEdit(profile) : undefined}
                  className={`relative bg-white border border-gray-100 rounded-xl p-4 transition-all group ${canManageBrandSettings ? 'hover:border-primary-200 hover:shadow-sm cursor-pointer [@media(hover:none)]:pr-[52px]' : ''}`}
                >
                  {/* Edit button - top right on hover */}
                  {canManageBrandSettings && (
                  <Tooltip content={t('dashboard_ai_profiles_edit', { defaultValue: 'Edit Brand World' })}>
                    <button
                      className="absolute top-3 right-3 [@media(hover:none)]:top-1 [@media(hover:none)]:right-1 flex items-center justify-center p-1.5 [@media(hover:none)]:min-h-[44px] [@media(hover:none)]:min-w-[44px] text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEdit(profile);
                      }}
                      aria-label={t('dashboard_ai_profiles_edit', { defaultValue: 'Edit Brand World' })}
                    >
                      <Icon name={ICONS.EDIT} size="sm" color="currentColor" />
                    </button>
                  </Tooltip>
                  )}

                  {/* Icon + Name */}
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${profile.isDefault ? 'bg-primary-100' : 'bg-gray-100'}`}>
                      <Icon name={ICONS.WORLD} size="md" color={profile.isDefault ? 'text-primary-600' : 'text-gray-500'} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 group-hover:text-primary-600 transition-colors truncate">
                        {profile.name}
                      </p>
                      <div className="flex flex-wrap items-center gap-1 mt-0.5">
                        {profile.status === 'DRAFT' && (
                          <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                            {t('dashboard_ai_profiles_draft', { defaultValue: 'Draft' })}
                          </span>
                        )}
                        {profile.isDefault && (
                          <span className="inline-block px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                            {t('dashboard_ai_profiles_default', { defaultValue: 'Default' })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Detail pills */}
                  {details.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {details.map((detail) => (
                        <span key={detail} className="px-2 py-0.5 bg-gray-50 text-gray-600 text-xs rounded-md">
                          {detail}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Usage */}
                  <p className="text-xs text-gray-400">{usageText}</p>
                  {lastUsedText && (
                    <p className="text-xs text-gray-400">{lastUsedText}</p>
                  )}
                </div>
              );
            })}

            {/* Add Profile Card - desktop only */}
            {canManageBrandSettings && (
            <button
              onClick={handleOpenCreate}
              disabled={atSystemCap}
              title={atSystemCap ? t('settings:settings_ai_profile_max_reached') : undefined}
              className="hidden sm:flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl p-4 text-gray-400 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center">
                <Icon name={ICONS.PLUS} size="md" color="currentColor" />
              </div>
              <span className="text-sm font-medium">
                {t('dashboard_ai_profiles_add', { defaultValue: 'Add Brand World' })}
              </span>
            </button>
            )}
          </div>

          {/* Add button - mobile only */}
          {canManageBrandSettings && (
          <button
            onClick={handleOpenCreate}
            disabled={atSystemCap}
            title={atSystemCap ? t('settings:settings_ai_profile_max_reached') : undefined}
            className="sm:hidden w-full mt-3 px-4 py-3 border-2 border-dashed border-gray-200 text-gray-500 text-sm font-medium rounded-xl hover:border-primary-300 hover:text-primary-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name={ICONS.PLUS} size="sm" color="currentColor" />
            {t('dashboard_ai_profiles_add', { defaultValue: 'Add Brand World' })}
          </button>
          )}

          {canManageByRole && brandWorldLock.locked && (
            <button
              type="button"
              onClick={brandWorldLock.showUpsell}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 hover:text-primary-800"
            >
              <Icon name={ICONS.LOCK} size="xs" ariaHidden />
              {brandWorldLock.label}
            </button>
          )}

          {/* View all link if more than 3 profiles */}
          {profiles.length > 3 && (
            <div className="mt-3 text-center">
              <button
                onClick={handleNavigateToSettings}
                className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                {t('dashboard_ai_profiles_view_all', { count: profiles.length, defaultValue: `View all ${profiles.length} Brand Worlds →` })}
              </button>
            </div>
          )}
        </>
      )}

      {canManageBrandSettings && (
        <>
          {/* Create Profile Modal — shared component with autosave */}
          <AIBrandProfileCreateModal
            open={createModal}
            onOpenChange={setCreateModal}
          />

          {/* Edit Profile Modal — shared component */}
          <AIBrandProfileEditModal
            open={editModal.open}
            profile={editModal.profile}
            onOpenChange={(open) => setEditModal({ open, profile: open ? editModal.profile : null })}
          />
        </>
      )}
    </div>
  );
};
